import { useState } from "react";
import { updateProfile, changePassword } from "../lib/api";
import { getUser, setUser } from "../lib/auth";
import Sidebar from "../components/Sidebar";

const BLUE   = "#0176CE";
const ORANGE = "#F37428";

export default function Profile() {
  const user = getUser();

  const [fullName,   setFullName]   = useState(user?.full_name || "");
  const [title,      setTitle]      = useState(user?.title     || "");
  const [phone,      setPhone]      = useState(user?.phone     || "");
  const [profSaving, setProfSaving] = useState(false);
  const [profMsg,    setProfMsg]    = useState("");
  const [profErr,    setProfErr]    = useState("");

  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd,     setNewPwd]     = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [pwdSaving,  setPwdSaving]  = useState(false);
  const [pwdMsg,     setPwdMsg]     = useState("");
  const [pwdErr,     setPwdErr]     = useState("");

  async function handleSaveProfile(e) {
    e.preventDefault();
    setProfSaving(true); setProfMsg(""); setProfErr("");
    try {
      const res = await updateProfile({ full_name: fullName, title, phone });
      setUser({ ...user, full_name: res.full_name, title: res.title, phone: res.phone });
      setProfMsg("Profile updated successfully.");
    } catch (err) {
      setProfErr(err.message || "Failed to save profile.");
    } finally {
      setProfSaving(false);
    }
  }

  async function handleChangePassword(e) {
    e.preventDefault();
    if (newPwd !== confirmPwd) { setPwdErr("New passwords do not match."); return; }
    if (newPwd.length < 8)     { setPwdErr("New password must be at least 8 characters."); return; }
    setPwdSaving(true); setPwdMsg(""); setPwdErr("");
    try {
      await changePassword(currentPwd, newPwd);
      setPwdMsg("Password changed successfully.");
      setCurrentPwd(""); setNewPwd(""); setConfirmPwd("");
    } catch (err) {
      setPwdErr(err.message || "Failed to change password.");
    } finally {
      setPwdSaving(false);
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.topStripe} />
      <div style={styles.body}>
        <Sidebar active="profile" />

        <main style={styles.main}>
          <div style={styles.header}>
            <h1 style={styles.pageTitle}>Your Profile</h1>
            <p style={styles.pageSub}>{user?.email}</p>
          </div>

          {/* Personal info */}
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Personal information</h2>
            <p style={styles.cardSub}>Update your name, title, and contact details.</p>
            <form onSubmit={handleSaveProfile} style={styles.form}>
              <div style={styles.fieldRow}>
                <div style={styles.field}>
                  <label style={styles.label}>Full name <span style={{ color: "#ef4444" }}>*</span></label>
                  <input style={styles.input} value={fullName} onChange={e => setFullName(e.target.value)} required disabled={profSaving} placeholder="Your full name" />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Title / role</label>
                  <input style={styles.input} value={title} onChange={e => setTitle(e.target.value)} disabled={profSaving} placeholder="e.g. Finance Manager" />
                </div>
              </div>
              <div style={{ ...styles.field, maxWidth: 320 }}>
                <label style={styles.label}>Phone</label>
                <input style={styles.input} value={phone} onChange={e => setPhone(e.target.value)} disabled={profSaving} placeholder="+1 555 000 0000" />
              </div>
              <div style={{ marginTop: 20, display: "flex", alignItems: "center", gap: 14 }}>
                <button type="submit" style={styles.primaryBtn} disabled={profSaving || !fullName.trim()}>
                  {profSaving ? "Saving…" : "Save changes"}
                </button>
                {profMsg && <span style={{ color: "#059669", fontSize: 13 }}>✓ {profMsg}</span>}
                {profErr && <span style={{ color: "#ef4444", fontSize: 13 }}>{profErr}</span>}
              </div>
            </form>
          </div>

          {/* Change password */}
          <div style={{ ...styles.card, marginTop: 24 }}>
            <h2 style={styles.cardTitle}>Change password</h2>
            <p style={styles.cardSub}>You'll stay logged in on this device after changing your password.</p>
            <form onSubmit={handleChangePassword} style={styles.form}>
              <div style={{ maxWidth: 360 }}>
                <div style={styles.field}>
                  <label style={styles.label}>Current password</label>
                  <input style={styles.input} type="password" value={currentPwd} onChange={e => setCurrentPwd(e.target.value)} required disabled={pwdSaving} placeholder="Your current password" autoComplete="current-password" />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>New password</label>
                  <input style={styles.input} type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)} required disabled={pwdSaving} placeholder="Minimum 8 characters" autoComplete="new-password" />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Confirm new password</label>
                  <input style={styles.input} type="password" value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)} required disabled={pwdSaving} placeholder="Repeat new password" autoComplete="new-password" />
                </div>
              </div>
              <div style={{ marginTop: 20, display: "flex", alignItems: "center", gap: 14 }}>
                <button type="submit" style={styles.primaryBtn} disabled={pwdSaving || !currentPwd || !newPwd || !confirmPwd}>
                  {pwdSaving ? "Updating…" : "Change password"}
                </button>
                {pwdMsg && <span style={{ color: "#059669", fontSize: 13 }}>✓ {pwdMsg}</span>}
                {pwdErr && <span style={{ color: "#ef4444", fontSize: 13 }}>{pwdErr}</span>}
              </div>
            </form>
          </div>

          {/* Account info */}
          <div style={{ ...styles.card, marginTop: 24 }}>
            <h2 style={styles.cardTitle}>Account</h2>
            <div style={styles.infoGrid}>
              <div style={styles.infoRow}><span style={styles.infoLabel}>Email</span><span style={styles.infoValue}>{user?.email}</span></div>
              <div style={styles.infoRow}><span style={styles.infoLabel}>Role</span><span style={{ ...styles.infoValue, textTransform: "capitalize" }}>{user?.role}</span></div>
              <div style={styles.infoRow}><span style={styles.infoLabel}>Company</span><span style={styles.infoValue}>{user?.company_name || "—"}</span></div>
            </div>
            <p style={{ color: "#94a3b8", fontSize: 12, marginTop: 16 }}>To change your email address, contact support.</p>
          </div>
        </main>
      </div>
    </div>
  );
}

const styles = {
  page:      { display: "flex", flexDirection: "column", minHeight: "100vh", fontFamily: "'Inter', system-ui, sans-serif" },
  topStripe: { height: 4, background: `linear-gradient(90deg, ${BLUE}, ${ORANGE})`, flexShrink: 0 },
  body:      { display: "flex", flex: 1 },
  main:      { flex: 1, padding: "40px 48px", overflowY: "auto", background: "#f8fafc" },
  header:    { marginBottom: 28 },
  pageTitle: { color: "#0f172a", fontSize: 28, fontWeight: 700, margin: 0, letterSpacing: "-0.5px" },
  pageSub:   { color: "#64748b", fontSize: 14, margin: "4px 0 0" },
  card:      { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "24px 24px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" },
  cardTitle: { color: "#0f172a", fontSize: 16, fontWeight: 700, margin: "0 0 4px" },
  cardSub:   { color: "#94a3b8", fontSize: 13, margin: "0 0 16px" },
  form:      { marginTop: 4 },
  fieldRow:  { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 },
  field:     { display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 },
  label:     { color: "#374151", fontSize: 13, fontWeight: 500 },
  input:     { padding: "9px 13px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 14, color: "#0f172a", outline: "none", background: "#fff" },
  primaryBtn:{ background: ORANGE, border: "none", borderRadius: 8, color: "#fff", cursor: "pointer", fontSize: 14, fontWeight: 600, padding: "10px 22px" },
  infoGrid:  { display: "flex", flexDirection: "column", gap: 12 },
  infoRow:   { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #f1f5f9" },
  infoLabel: { color: "#64748b", fontSize: 13 },
  infoValue: { color: "#0f172a", fontSize: 13, fontWeight: 500 },
};
